import React, { useEffect, useMemo, useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import GroupAdd from "@mui/icons-material/GroupAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandIconComponent from "./Expand-icon.component";
import DeviceItemComponent from "./Device-item.component";
import ListSkeletonComponent from "./List-skeleton.component";
import useDictionary from "@ui/hooks/useDictionary";
import { Menu } from "@ui/components/Menu/menu";
import { IMenuItem } from "@ui/components/Menu/IMenuItem";
import { Checkbox } from "@ui/components/Checkbox/checkbox";
import { IProps as IMainProps } from "./IProps";
import { IStoredDevice } from "@store/Devices.store";
import { emptyFn } from "@core/interfaces/emptyFn";
import { position } from "@store/Map.store";
import { useStore } from "@ui/hooks/useStore";
import { toJS, when } from "mobx";
import { observer } from "mobx-react-lite";
import { Virtuoso } from "react-virtuoso";

interface IProps
    extends Pick<
        IMainProps,
        | "tab"
        | "page"
        | "loading"
        | "selectedGroupId"
        | "selectedDevices"
        | "selectedGroupDevices"
        | "selectedDevice"
        | "editedDeviceImei"
        | "infoDeviceImei"
        | "deviceBtnHover"
        | "settingDeviceImei"
        | "lang"
        | "columnVisibilityModel"
        | "dateFilter"
        | "fromDateFilter"
        | "toDateFilter"
        | "zoom"
        | "sortModel"
        | "settingsList"
        | "onGroupClick"
        | "onGroupSelect"
        | "onGroupAddDeviceOpen"
        | "onGroupSettingsOpen"
        | "onGroupDeleteOpen"
        | "onDeviceSelect"
        | "onDeviceClick"
        | "onDeviceDeleteOpen"
        | "onDeviceDescriptionOpen"
        | "onDeviceTelemetryOpen"
        | "onDeviceBtnHoverStart"
        | "onDeviceBtnHoverStop"
        | "onDeviceSettingsOpen"
        | "onDeviceInfoOpen"
    > {
    name: string;
    devices: IStoredDevice[];
    id: string;
    visible: boolean;
    scrollRef: React.RefObject<HTMLUListElement>,
    defaultGroup?: string;
    isGroupDefault: boolean;
}

const DeviceItem = ({
    device,
    tab,
    page,
    loading,
    selectedDevices,
    selectedDevice,
    editedDeviceImei,
    infoDeviceImei,
    deviceBtnHover,
    settingDeviceImei,
    lang,
    columnVisibilityModel,
    dateFilter,
    fromDateFilter,
    toDateFilter,
    zoom,
    sortModel,
    settingsList,
    onDeviceSelect,
    onDeviceClick,
    onDeviceDeleteOpen,
    onDeviceDescriptionOpen,
    onDeviceTelemetryOpen,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
    onDeviceSettingsOpen,
    onDeviceInfoOpen,
}: { device: IStoredDevice } & Omit<IProps, "scrollRef">) => {
    const { app: appStore, devices: deviceStore } = useStore();
    const { name, imei, speed, batteryVoltage, date } = device;
    let coords: position | undefined;
    const gpsCoord = device.gpsCoord;
    const lbsCoord = device.lbsCoord;
    if (gpsCoord && gpsCoord.lat && gpsCoord.lon) {
        coords = [gpsCoord.lat, gpsCoord.lon];
    } else if (lbsCoord && lbsCoord.lat && lbsCoord.lon) {
        coords = [lbsCoord.lat, lbsCoord.lon];
    }
    const [settings] = settingsList.filter((val) => val.imei === imei);
    const [currentSettings] = settingsList.filter(
        (val) => val.imei === deviceStore.settingDeviceImei
    );

    // Обработчик отвечающий за переключение между окнами
    // при выбраном устройстве
    const handleDeviceClick = (
        event: React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
        // Переход при выбраном окне настроек
        if (deviceStore.settingDeviceImei != null) {
            if (imei && deviceStore.settingDeviceImei != imei) {
                const isSettings =
                    currentSettings &&
                    currentSettings.status &&
                    !currentSettings.status.error;
                if (isSettings) {
                    appStore.setDeviceSettingsClosing();

                    when(() => appStore.isDeviceSettingsClosing === false).then(
                        () => {
                            deviceStore.setSettingDeviceImei(imei);
                            appStore.openDeviceSettings();
                        }
                    );
                } else {
                    deviceStore.setSettingDeviceImei(imei);
                    appStore.openDeviceSettings();
                }
            }
        }

        // Переход при выбраном окне информации
        if (deviceStore.infoDeviceImei != null) {
            if (imei && deviceStore.infoDeviceImei != imei) {
                deviceStore.setInfoDeviceImei(imei);
            }
        }

        // Переход при выбраном окне описания
        if (deviceStore.editedDeviceImei != null) {
            if (imei && deviceStore.infoDeviceImei != imei) {
                deviceStore.setEditedDeviceImei(imei);
            }
        }

        onDeviceClick(event);
    };

    return (
        <DeviceItemComponent
            tab={tab}
            page={page}
            name={name}
            speed={speed}
            batteryVoltage={batteryVoltage}
            date={date}
            loading={loading}
            key={imei}
            id={imei}
            selectedDevices={selectedDevices}
            selectedDevice={selectedDevice}
            editedDeviceImei={editedDeviceImei}
            infoDeviceImei={infoDeviceImei}
            deviceBtnHover={deviceBtnHover}
            settingDeviceImei={settingDeviceImei}
            lang={lang}
            coords={coords}
            columnVisibilityModel={columnVisibilityModel}
            dateFilter={dateFilter}
            fromDateFilter={fromDateFilter}
            toDateFilter={toDateFilter}
            zoom={zoom}
            sortModel={sortModel}
            settings={settings}
            onDeviceSelect={onDeviceSelect}
            onDeviceClick={handleDeviceClick}
            onDeviceDeleteOpen={onDeviceDeleteOpen}
            onDeviceDescriptionOpen={onDeviceDescriptionOpen}
            onDeviceTelemetryOpen={onDeviceTelemetryOpen}
            onDeviceBtnHoverStart={onDeviceBtnHoverStart}
            onDeviceBtnHoverStop={onDeviceBtnHoverStop}
            onDeviceSettingsOpen={onDeviceSettingsOpen}
            onDeviceInfoOpen={onDeviceInfoOpen}
        />
    );
};

const Component: React.FC<IProps> = ({
    tab,
    page,
    name,
    loading,
    devices,
    id,
    visible,
    isGroupDefault,
    defaultGroup,
    selectedGroupId,
    selectedDevices,
    selectedGroupDevices,
    selectedDevice,
    editedDeviceImei,
    infoDeviceImei,
    deviceBtnHover,
    settingDeviceImei,
    lang,
    columnVisibilityModel,
    dateFilter,
    fromDateFilter,
    toDateFilter,
    zoom,
    sortModel,
    settingsList,
    scrollRef,
    onGroupClick,
    onGroupSelect,
    onGroupAddDeviceOpen,
    onGroupSettingsOpen,
    onGroupDeleteOpen,
    onDeviceSelect,
    onDeviceClick,
    onDeviceDeleteOpen,
    onDeviceDescriptionOpen,
    onDeviceTelemetryOpen,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
    onDeviceSettingsOpen,
    onDeviceInfoOpen,
}: IProps) => {
    const getWord = useDictionary();
    const length = devices.length;
    const title = `${name} (${length})`;

    const menuList: IMenuItem[] = [
        {
            text: "addDeviceToGroup",
            id: id,
            icon: <GroupAdd className="groups__item-menu-ico_item" />,
            onClick: onGroupAddDeviceOpen,
        },
        {
            text: "editGroupDescription",
            id: id,
            icon: <EditIcon className="groups__item-menu-ico_item" />,
            onClick: onGroupSettingsOpen,
        },
        {
            text: "groupDelete",
            id: id,
            icon: <DeleteIcon className="groups__item-menu-ico_item" />,
            onClick: onGroupDeleteOpen,
        },
    ].map((item) => ({
        ...item,
        text: getWord(item.text),
    }));

    const deviceList = useMemo(() => {
        if (!loading && devices.length > 0) {
            return devices.map((storedDevice) => ({
                id,
                device: storedDevice,
                tab,
                page,
                loading,
                selectedDevices,
                selectedDevice,
                editedDeviceImei,
                infoDeviceImei,
                deviceBtnHover,
                settingDeviceImei,
                lang,
                columnVisibilityModel,
                dateFilter,
                fromDateFilter,
                toDateFilter,
                zoom,
                sortModel,
                settingsList,
                onDeviceSelect,
                onDeviceClick,
                onDeviceDeleteOpen,
                onDeviceDescriptionOpen,
                onDeviceTelemetryOpen,
                onDeviceBtnHoverStart,
                onDeviceBtnHoverStop,
                onDeviceSettingsOpen,
                onDeviceInfoOpen,
                name,
                devices,
                visible,
                isGroupDefault,
                selectedGroupId,
                selectedGroupDevices,
                onGroupClick,
                onGroupSelect,
                onGroupAddDeviceOpen,
                onGroupSettingsOpen,
                onGroupDeleteOpen,
            }));
        } else {
            return [];
        }
    }, [loading, devices]);

    if (!loading) {
        const checked = selectedGroupId === id;
        const checkboxIndeterminate =
            checked && selectedDevices.length !== selectedGroupDevices.length;
        const getCheckbox = () => {
            if (page !== "history" && page !== "support" && length > 0) {
                return (
                    <Checkbox
                        name={name}
                        value={id}
                        status={
                            checked && checkboxIndeterminate
                                ? "indeterminate"
                                : checked
                                ? "checked"
                                : "unchecked"
                        }
                        onClick={onGroupSelect}
                    />
                );
            } else return <></>;
        };
        const checkbox = getCheckbox();
        let handleGroupClick: IProps["onGroupClick"] | emptyFn = () =>
            onGroupSelect(id);
        return (
            <>
                <ListItemButton
                    className="groups__item"
                    component="li"
                    id={id}
                    onClick={handleGroupClick}
                >
                    <ListItemText
                        classes={{
                            root: "groups__text-item",
                            primary: "groups__text",
                        }}
                        primary={title}
                    />
                    <Tooltip
                        classes={{
                            popper: "groups__menu-tooltip-popper",
                            tooltip: "groups__menu-tooltip",
                        }}
                        title={getWord("displayGroupOnTheMap")}
                        placement="top"
                    >
                        {checkbox}
                    </Tooltip>
                    {defaultGroup !== id && !isGroupDefault && (
                        <Menu
                            label="additionally"
                            tooltip
                            button={
                                <MoreVertIcon className="groups__item-menu-ico" />
                            }
                            items={menuList}
                        />
                    )}
                    <ExpandIconComponent devices={devices} visible={visible} />
                </ListItemButton>
                <Collapse className="devices__item" component="li" in={visible}>
                    <List className="device__list">
                        <Virtuoso
                            customScrollParent={scrollRef.current as HTMLElement}
                            totalCount={deviceList.length}
                            style={{ width: "100%" }}
                            width="100%"
                            itemContent={(index:any) => (
                                <DeviceItem {...deviceList[index]} />
                            )}
                        />
                    </List>
                </Collapse>
            </>
        );
    } else {
        return <ListSkeletonComponent />;
    }
};

const GroupItemComponent = observer(Component);

export default GroupItemComponent;
